import { Injectable } from '@angular/core';
import {
  BaseService,
  generateArray,
  LeagueApi, LeaguePlayerApi,
  Playoff, PublicTeamApi,
  StatisticFilters,
  Tournament,
  TournamentApi,
  TournamentDivision,
  TournamentGroup,
  TournamentRound,
  TournamentRoundApi,
  TournamentRoundTeam,
  TournamentRoundTypes,
  TournamentSeason,
  TournamentSeasonApi
} from '@mtgame/core';
import { Observable } from 'rxjs';

const STORE_SEASONS_KEY = 'seasons';
const STORE_TOURNAMENTS_KEY = 'tournaments';
const STORE_ALL_TOURNAMENTS_KEY = 'allTournaments';
const STORE_ROUNDS_KEY = 'rounds';
const STORE_ALL_DIVISIONS_KEY = 'allDivisions';
const STORE_DIVISIONS_KEY = 'divisions';
const STORE_TEAMS_KEY = 'teams';
const STORE_GROUPS_KEY = 'groups';
const STORE_TOURS_KEY = 'tours';
const STORE_PLAYOFFS_KEY = 'playoffs';
const STORE_PLAYOFF_ROUNDS_KEY = 'playoffRounds';
const STORE_FILTERS_KEY = 'filters';

@Injectable()
export class StatisticFiltersWidgetService extends BaseService {
  private leagueId: number;
  private teamId: number;
  private leaguePlayerId: number;
  private additionalFiltersVisible: boolean;

  get seasons$(): Observable<TournamentSeason[]> {
    return this.store.get(STORE_SEASONS_KEY, []);
  }

  get tournaments$(): Observable<Tournament[]> {
    return this.store.get(STORE_TOURNAMENTS_KEY, []);
  }

  get divisions$(): Observable<TournamentDivision[]> {
    return this.store.get(STORE_DIVISIONS_KEY, []);
  }

  get rounds$(): Observable<TournamentRound[]> {
    return this.store.get(STORE_ROUNDS_KEY, []);
  }

  get groups$(): Observable<TournamentGroup[]> {
    return this.store.get(STORE_GROUPS_KEY, []);
  }

  get tours$(): Observable<number[]> {
    return this.store.get(STORE_TOURS_KEY, []);
  }

  get playoffs$(): Observable<Playoff[]> {
    return this.store.get(STORE_PLAYOFFS_KEY, []);
  }

  get playoffRounds$(): Observable<number[]> {
    return this.store.get(STORE_PLAYOFF_ROUNDS_KEY, []);
  }

  get filters$(): Observable<Partial<StatisticFilters>> {
    return this.store.get(STORE_FILTERS_KEY);
  }

  get filters(): StatisticFilters {
    return this.store.value(STORE_FILTERS_KEY, {});
  }

  constructor(
    private leagueApi: LeagueApi,
    private seasonApi: TournamentSeasonApi,
    private tournamentApi: TournamentApi,
    private tournamentRoundApi: TournamentRoundApi,
    private publicTeamApi: PublicTeamApi,
    private leaguePlayerApi: LeaguePlayerApi,
  ) {
    super();
  }

  async initialize(
    identity: {leagueId: number, teamId?: number, leaguePlayerId?: number},
    filters: Partial<StatisticFilters> = {},
    additionalFiltersVisible = false,
    lastRoundDefault = false,
  ): Promise<void> {
    this.store.save(STORE_FILTERS_KEY, {});

    this.leagueId = identity.leagueId;
    this.teamId = identity.teamId;
    this.leaguePlayerId = identity.leaguePlayerId;
    this.additionalFiltersVisible = additionalFiltersVisible;
    if (this.teamId) {
      await this.getTeamData();
    } else if (this.leaguePlayerId) {
      await this.getLeaguePlayerData();
    } else {
      await this.getSeasons();
    }
    let newFilters = await this.setFilters(filters);
    if (!filters.tournament_id && lastRoundDefault) {
      const tournaments = this.store.value(STORE_TOURNAMENTS_KEY);
      if (tournaments.length > 0) {
        newFilters = await this.setFilters({...newFilters, tournament_id: tournaments[0].id});
      }
    }
    if (!filters.tournament_round_id && lastRoundDefault) {
      const rounds = this.store.value(STORE_ROUNDS_KEY);
      if (rounds.length > 0) {
        newFilters = await this.setFilters({...newFilters, tournament_round_id: rounds[rounds.length - 1].id});
      }
    }
    this.store.save(STORE_FILTERS_KEY, newFilters);
  }

  async getTeamData(): Promise<void> {
    const tournamentTeams = await this.publicTeamApi.getTournamentTeams(this.teamId, this.leagueId);
    const tournaments = tournamentTeams.map(item => item.tournament).filter(item => item.season.id);
    const seasons = tournaments.map(item => item.season)
      .filter(
        (season, index, all) =>
          all.findIndex(item => item.id === season.id) === index
      );
    this.store.save(STORE_SEASONS_KEY, seasons);
    this.store.save(STORE_ALL_TOURNAMENTS_KEY, tournaments);
  }

  async getLeaguePlayerData(): Promise<void> {
    const tournamentTeamUsers = await this.leaguePlayerApi.getTournamentUsers(this.leaguePlayerId);
    const tournaments = tournamentTeamUsers.map(item => item.tournamentTeam.tournament)
      .filter(item => item.season.id)
      .filter(
        (tournament, index, all) =>
          all.findIndex(item => item.id === tournament.id) === index
      );
    const seasons = tournaments.map(item => item.season)
      .filter(
        (season, index, all) =>
          all.findIndex(item => item.id === season.id) === index
      );
    this.store.save(STORE_SEASONS_KEY, seasons);
    this.store.save(STORE_ALL_TOURNAMENTS_KEY, tournaments);
  }

  async getSeasons(): Promise<TournamentSeason[]> {
    const seasonsData = await this.leagueApi.getSeasons(this.leagueId);
    this.store.save(STORE_SEASONS_KEY, seasonsData.data);
    return seasonsData.data;
  }

  async getTournaments(seasonId: number): Promise<Tournament[]> {
    if (!seasonId) {
      this.store.save(STORE_TOURNAMENTS_KEY, []);
      return [];
    }

    if (this.teamId || this.leaguePlayerId) {
      const tournaments = this.store.value(STORE_ALL_TOURNAMENTS_KEY, [])
        .filter(item => item.season.id === seasonId);
      this.store.save(STORE_TOURNAMENTS_KEY, tournaments);
      return tournaments;
    }

    const tournaments = await this.seasonApi.getTournaments(seasonId);
    this.store.save(STORE_TOURNAMENTS_KEY, tournaments);
    return tournaments;
  }

  async getRounds(tournamentId: number): Promise<TournamentRound[]> {
    if (!tournamentId) {
      this.store.save(STORE_ROUNDS_KEY, []);
      return [];
    }
    const rounds = await this.tournamentApi.getTournamentRounds(tournamentId);
    this.store.save(STORE_ROUNDS_KEY, rounds);
    return rounds;
  }

  async getDivisions(tournamentId: number): Promise<TournamentDivision[]> {
    if (!tournamentId) {
      this.store.save(STORE_ALL_DIVISIONS_KEY, []);
      this.store.save(STORE_DIVISIONS_KEY, []);
      return [];
    }
    const divisions = await this.tournamentApi.getTournamentDivisions(tournamentId);
    this.store.save(STORE_ALL_DIVISIONS_KEY, divisions);
    this.store.save(STORE_DIVISIONS_KEY, divisions);
    return divisions;
  }

  getRoundDivisions(round: TournamentRound): TournamentDivision[] {
    if (!round) {
      this.store.save(STORE_DIVISIONS_KEY, this.store.value(STORE_ALL_DIVISIONS_KEY));
      return this.store.value(STORE_ALL_DIVISIONS_KEY);
    }

    this.store.save(STORE_DIVISIONS_KEY, round.divisions);
    return round.divisions;
  }

  async getRoundTeams(round: TournamentRound): Promise<TournamentRoundTeam[]> {
    if (!round || round.type !== TournamentRoundTypes.group) {
      this.store.save(STORE_TEAMS_KEY, []);
      return [];
    }
    const teams = await this.tournamentRoundApi.getTeams(round.id);
    this.store.save(STORE_TEAMS_KEY, teams);
    return teams;
  }

  getGroups(round: TournamentRound, divisionId: number): TournamentGroup[] {
    if (!round || round.type !== TournamentRoundTypes.group) {
      this.store.save(STORE_GROUPS_KEY, []);
      return [];
    }
    if (round.divisions.length > 0 && !divisionId) {
      this.store.save(STORE_GROUPS_KEY, []);
      return [];
    }
    const teams = this.store.value(STORE_TEAMS_KEY, []);
    const groups = teams.filter(item => !divisionId || item.divisionId === divisionId)
      .map(item => item.group)
      //.filter(item => !divisionId || item.divisionId === divisionId)
      .filter((item, index, all) =>
        all.findIndex(v => v.id === item.id) === index
      );
    this.store.save(STORE_GROUPS_KEY, groups);
    return groups;
  }

  getTours(tournamentId: number, round: TournamentRound, divisionId: number): number[] {
    if (!round || round.type !== TournamentRoundTypes.group) {
      this.store.save(STORE_TOURS_KEY, []);
      return [];
    }
    const tournament = this.store.value(STORE_TOURNAMENTS_KEY, [])
      .find(item => item.id === tournamentId);
    if (!tournament) {
      this.store.save(STORE_TOURS_KEY, []);
      return [];
    }
    const teams = this.store.value(STORE_TEAMS_KEY, []);
    const teamsByGroupCount = teams
      .filter(item => !divisionId || item.division.id === divisionId)
      .reduce((acc, item) => ({
        ...acc,
        [item.group.id]: (acc[item.group.id] || 0) + 1
      }), {});
    let maxTeamsInGroupCount = Math.max(...Object.keys(teamsByGroupCount).map(key => +teamsByGroupCount[key]));
    if (maxTeamsInGroupCount === -Infinity) {
      maxTeamsInGroupCount = 0;
    }
    const toursCount = (maxTeamsInGroupCount % 2 === 0) ? maxTeamsInGroupCount - 1 : maxTeamsInGroupCount;
    const tours = generateArray(toursCount * tournament.settings.toursCount);
    this.store.save(STORE_TOURS_KEY, tours);
  }

  async getPlayoffs(round: TournamentRound): Promise<Playoff[]> {
    if (!round || round.type !== TournamentRoundTypes.playoff) {
      this.store.save(STORE_PLAYOFFS_KEY, []);
      return [];
    }
    const playoffs = await this.tournamentRoundApi.getPlayoffs(round.id);
    this.store.save(STORE_PLAYOFFS_KEY, playoffs);
    return playoffs;
  }

  getPlayoffRounds(round: TournamentRound, playoffId: number): number[] {
    if (!round || round.type !== TournamentRoundTypes.playoff) {
      this.store.save(STORE_PLAYOFF_ROUNDS_KEY, []);
      return [];
    }
    const playoffs = this.store.value(STORE_PLAYOFFS_KEY, []);
    let maxPlayoffTeamsCount = Math.max(
      ...playoffs.filter(item => !playoffId || playoffId === item.id)
        .map(item => item.settings.teamsCount)
    );
    if (maxPlayoffTeamsCount === -Infinity) {
      maxPlayoffTeamsCount = 0;
    }
    const playoffRoundsCount = Math.log(maxPlayoffTeamsCount) / Math.log(2);
    const playoffRounds = generateArray(playoffRoundsCount).map(value => maxPlayoffTeamsCount / 2 ** value);
    this.store.save(STORE_PLAYOFF_ROUNDS_KEY, playoffRounds);
    return playoffRounds;
  }

  async setFilters(filters: Partial<StatisticFilters>, save = false): Promise<Partial<StatisticFilters>> {
    const oldFilters: StatisticFilters = this.store.value(STORE_FILTERS_KEY, {});

    if (filters.tournament_season_id !== oldFilters.tournament_season_id) {
      const tournaments = await this.getTournaments(filters.tournament_season_id);
      if (filters.tournament_id && !tournaments.find(item => item.id === filters.tournament_id)) {
        filters.tournament_id = null;
      }
    }
    if (filters.tournament_id !== oldFilters.tournament_id) {
      const [rounds, divisions] = await Promise.all([
        this.getRounds(filters.tournament_id),
        this.additionalFiltersVisible ? this.getDivisions(filters.tournament_id) : Promise.resolve([]),
      ]);
      if (filters.tournament_round_id && !rounds.find(item => item.id === filters.tournament_round_id)) {
        filters.tournament_round_id = null;
      }
      if (filters.division_id && !divisions.find(item => item.id === filters.division_id)) {
        filters.division_id = null;
      }
    }
    if (this.additionalFiltersVisible && filters.tournament_round_id !== oldFilters.tournament_round_id) {
      const round = this.store.value(STORE_ROUNDS_KEY, [])
        .find(item => item.id === filters.tournament_round_id);
      await this.getRoundTeams(round);
      const divisions = this.getRoundDivisions(round);
      if (filters.division_id && !divisions.find(item => item.id === filters.division_id)) {
        filters.division_id = null;
      }
      const groups = this.getGroups(round, filters.division_id);
      if (filters.tournament_group_id && !groups.find(item => item.id === filters.tournament_group_id)) {
        filters.tournament_group_id = null;
      }
      const tours = this.getTours(filters.tournament_id, round, filters.division_id);
      if (filters.tournament_tour && !tours.includes(filters.tournament_tour)) {
        filters.tournament_tour = null;
      }
      const playoffs = await this.getPlayoffs(round);
      if (filters.playoff_id && !playoffs.find(item => item.id === filters.playoff_id)) {
        filters.playoff_id = null;
      }
      const playoffRounds = this.getPlayoffRounds(round, filters.playoff_id);
      if (filters.playoff_round && !playoffRounds.includes(filters.playoff_round)) {
        filters.playoff_round = null;
      }
    }
    if (this.additionalFiltersVisible && filters.division_id !== oldFilters.division_id) {
      const round = this.store.value(STORE_ROUNDS_KEY, [])
        .find(item => item.id === filters.tournament_round_id);
      const groups = this.getGroups(round, filters.division_id);
      if (filters.tournament_group_id && !groups.find(item => item.id === filters.tournament_group_id)) {
        filters.tournament_group_id = null;
      }
    }
    if (save) {
      this.store.save(STORE_FILTERS_KEY, filters);
    }
    return filters;
  }
}
